import {testIdProps} from '@github-ui/test-id-props'
import {clsx} from 'clsx'
import {chunk} from 'lodash-es'
import {Fragment, memo, useMemo, useRef} from 'react'

import {SystemColumnId} from '../../api/columns/contracts/memex-column'
import {useSubIssuesLoading} from '../../features/sub-issues/use-sub-issues-loading'
import {useTableHierarchy} from '../../features/sub-issues/use-table-hierarchy'
import {getColumnMeta} from '../../hooks/tanstack-table/helpers/column-api'
import {isAddColumnId} from '../../hooks/tanstack-table/helpers/column-ids'
import type {ColumnInstance, Row} from '../../hooks/tanstack-table/helpers/types'
import {useTableInstance} from '../../hooks/tanstack-table/providers/table-provider'
import type {MemexItemModel} from '../../models/memex-item-model'
import useIsVisible from '../board/hooks/use-is-visible'
import {TextPlaceholder} from '../common/placeholders'
import {BaseCell} from './cells/base-cell'
import {CELL_HEIGHT} from './constants'
import {useTableNavigation} from './navigation'
import {useIsDraggingRows} from './row-reordering/hooks/use-is-dragging-rows'
import {StyledTableCell} from './table-cell'
import type {TableDataType} from './table-data-type'
import {StyledTableRow, TableRow, type TableRowProps} from './table-row'
import styles from './table-row-chunk.module.css'

/** The number of rows that will be rendered in a batch as they are scrolled into view. */
export const VIRTUALIZATION_CHUNK_SIZE = 5

/**
 * This represents a set of rows that act as a single unit of virtualization in
 * the vertical scroll direction.
 *
 * In other words, as the user scrolls vertically, we will render a new
 * TableRowChunk as it becomes visible (as opposed to rendering each TableRow
 * individually).
 */
export const TableRowChunk = memo(function TableRowChunk(props: {
  rows: Array<Row<TableDataType>>
  itemData: TableRowProps['data']
}) {
  const ref = useRef<HTMLDivElement | null>(null)
  const {isVisible, size} = useIsVisible({ref, defaultHeight: CELL_HEIGHT * props.rows.length})
  const {
    state: {focus},
  } = useTableNavigation()
  const {hierarchyEnabled} = useTableHierarchy()

  const isFocused = focus && focus.type === 'coordinate' ? props.rows.some(row => row.id === focus.details.y) : false

  // If we don't render the rows when dragging, the `useReorderableRow` hook will get unmounted and dnd-kit will not
  // provide any drop data
  const isDragging = useIsDraggingRows(props.rows)

  const shouldRender = isVisible || isFocused || isDragging

  // When this chunk of rows is visible, we set the height to 'unset' so that we inherit the true
  // height of the chunk after all of its contents have been painted. Otherwise, we render a fixed
  // height as a placeholder.
  const height = isVisible ? 'unset' : `${size}px`

  const style = useMemo(() => ({height}), [height])

  return (
    <div style={style} ref={ref}>
      {shouldRender
        ? props.rows.map(row => (
            <Fragment key={row.id}>
              <TableRow key={row.id} row={row} index={row.index} data={props.itemData} />
              {hierarchyEnabled && row.getIsExpanded() && (
                <SubIssuesTableRowChunk parentRow={row} itemData={props.itemData} />
              )}
            </Fragment>
          ))
        : null}
    </div>
  )
})

const SubIssuesTableRowChunk = ({
  parentRow,
  itemData,
}: {
  parentRow: Row<MemexItemModel>
  itemData: TableRowProps['data']
}) => {
  const {isLoadingSubIssues} = useSubIssuesLoading()
  const {visibleColumns: legacyVisibleColumns, getVisibleLeafColumns} = useTableInstance()
  const visibleColumns = getVisibleLeafColumns ? getVisibleLeafColumns() : legacyVisibleColumns

  if (isLoadingSubIssues(parentRow)) {
    return <SubIssuesPlaceholderRow columns={visibleColumns} depth={parentRow.subIssueData?.subIssueDepth} />
  }

  return (
    <Fragment>
      {chunk(parentRow.subRows, VIRTUALIZATION_CHUNK_SIZE).map((rowChunk, index) => (
        <TableRowChunk
          // eslint-disable-next-line @eslint-react/no-array-index-key
          key={index}
          rows={rowChunk}
          itemData={itemData}
        />
      ))}
      <SubIssuesMissingRow columns={visibleColumns} parentRow={parentRow} />
    </Fragment>
  )
}

const SubIssuesPlaceholderRow = memo(function SubIssuesPlaceholderRow({
  columns,
  depth = 0,
}: {
  columns: Array<ColumnInstance<MemexItemModel>>
  depth?: number
}) {
  const titlePlaceholder = (
    <BaseCell style={{'--hierarchy-depth': depth + 1}} className={styles.hierarchyDepth}>
      <TextPlaceholder minWidth={80} maxWidth={200} {...testIdProps('placeholder')} />
    </BaseCell>
  )

  return (
    <StyledTableRow role="row" aria-busy="true" aria-label="Loading sub-issues" {...testIdProps('placeholder-row')}>
      {columns.map(column => (
        <SubIssuesStyledTableCell key={column.id} column={column}>
          {column.id === SystemColumnId.Title ? titlePlaceholder : getColumnMeta(column)?.Placeholder}
        </SubIssuesStyledTableCell>
      ))}
    </StyledTableRow>
  )
})

const SubIssuesMissingRow = ({
  columns,
  parentRow,
}: {
  columns: Array<ColumnInstance<MemexItemModel>>
  parentRow: Row<MemexItemModel>
}) => {
  const {missingSubIssues = 0, subIssueDepth = 0} = parentRow.subIssueData ?? {}
  if (missingSubIssues <= 0) return null
  const missingSubIssuesText = `${missingSubIssues} sub-issue${missingSubIssues !== 1 ? 's' : ''} not visible`

  const titleCell = (
    <BaseCell
      style={{'--hierarchy-depth': subIssueDepth + 1}}
      className={clsx(styles.missingSubIssuesText, styles.hierarchyDepth)}
    >
      {missingSubIssuesText}
    </BaseCell>
  )

  return (
    <StyledTableRow role="row" aria-label={missingSubIssuesText}>
      {columns.map(column => (
        <SubIssuesStyledTableCell key={column.id} column={column}>
          {column.id === SystemColumnId.Title ? titleCell : null}
        </SubIssuesStyledTableCell>
      ))}
    </StyledTableRow>
  )
}

const SubIssuesStyledTableCell = ({
  column,
  children,
}: {
  column: ColumnInstance<MemexItemModel>
  children: React.ReactNode
}) => {
  return (
    <StyledTableCell
      role="gridcell"
      style={{
        whiteSpace: 'nowrap',
        width: column.getSize(),
        flexGrow: isAddColumnId(column.id) ? 1 : 'unset',
      }}
    >
      {children}
    </StyledTableCell>
  )
}

try{ TableRowChunk.displayName ||= 'TableRowChunk' } catch {}
try{ SubIssuesTableRowChunk.displayName ||= 'SubIssuesTableRowChunk' } catch {}
try{ SubIssuesPlaceholderRow.displayName ||= 'SubIssuesPlaceholderRow' } catch {}
try{ SubIssuesMissingRow.displayName ||= 'SubIssuesMissingRow' } catch {}
try{ SubIssuesStyledTableCell.displayName ||= 'SubIssuesStyledTableCell' } catch {}